import angular from 'angular';
import i18n from '../i18n';
import statusMessageDirective from './status-message-directive';
import statusMessageGroupDirective from './status-message-group-directive';
import statusMessageService from './status-message-service';

/**
 * @ngdoc module
 * @name akamai.components.status-message
 *
 * @guideline https://design.akamai.com/components/messages/
 *
 * @description
 * Status messages communicate general application information, success, errors or warnings.
 * They can appear the top of the page, below the browser chrome spanning the width of the page.
 *
 * @example status-message.js
 *  var ctrlFunction = function($scope, statusMessage) {
 *    var messageText = "Hello {{name}}!";
 *    $scope.showStatus = function(){
 *      statusMessage.showInformation({i18nKey: messageText, i18nKeyValues: {name: 'Joe'}});
 *    };
 *  };
 */
export default angular.module('akamai.components.status-message', [
  i18n.name
])

/**
 * @name akamStatusMessage
 * @description Creates a status message control.
 * @restrict E
 * @param {string} text The required text to display.
 */

/**
 * @ngdoc method
 * @name statusMessage#show
 * @description shows a success message to the end user. This is an alias for showSuccess
 * @param {Object} [options] A hash with the options specified below.
 * @param {string|TranslateKey} [options.i18nKey] The i18n key or generic string content
 * to show within the status message.  If the value is found in the translate table, automatic
 * translation will take place.
 * @param {Object} [options.i18nKeyValues={}] a hash for passing in for translation value support.
 * @param {string|TranslateKey} [options.text] (DEPRECATED in favor of i18nKey) The text content
 * to show within the status message note: text string can be a translation key, it will
 * automatically be translated
 * @param {Object} [options.textValues={}] (DEPRECATED in favor of i18nKeyValues) a hash for
 * passing in for translation value support.
 * @param {number} [options.timeout=2000] The time (in ms) before the status message removes
 * itself from being shown.  *Note: Use timeout = 0 to force the user to close the message.*
 * @param {string} [options.template] a text string of the template id that should be rendered
 * @param {string} [options.templateUrl] a text string of the url location of the
 * template that should be rendered
 * @param {Scope} [options.scope=$rootScope] A scope instance to use for the status message
 * body content.
 * @returns {string} id of status message being shown on the screen
 */

/**
 * @ngdoc method
 * @name statusMessage#showSuccess
 * @description shows a success message to the end user
 * @param {Object} [options] A hash with the options specified below.
 * @param {string|TranslateKey} [options.i18nKey] The i18n key or generic string content
 * to show within the status message.  If the value is found in the translate table, automatic
 * translation will take place.
 * @param {Object} [options.i18nKeyValues={}] a hash for passing in for translation value support.
 * @param {string|TranslateKey} [options.text] (DEPRECATED in favor of i18nKey) The text content
 * to show within the status message note: text string can be a translation key, it will
 * automatically be translated
 * @param {Object} [options.textValues={}] (DEPRECATED in favor of i18nKeyValues) a hash for
 * passing in for translation value support.
 * @param {number} [options.timeout=2000] The time (in ms) before the status message removes
 * itself from being shown.  *Note: Use timeout = 0 to force the user to close the message.*
 * @param {string} [options.template] a text string of the template id that should be rendered
 * @param {string} [options.templateUrl] a text string of the url location of the
 * template that should be rendered
 * @param {Scope} [options.scope=$rootScope] A scope instance to use for the status message
 * body content.
 * @returns {string} id of status message being shown on the screen
 */

/**
 * @ngdoc method
 * @name statusMessage#showInformation
 * @description shows an informational message to the end user
 * @param {Object} [options] A hash with the options specified below.
 * @param {string|TranslateKey} [options.i18nKey] The i18n key or generic string content
 * to show within the status message.  If the value is found in the translate table, automatic
 * translation will take place.
 * @param {Object} [options.i18nKeyValues={}] a hash for passing in for translation value support.
 * @param {string|TranslateKey} [options.text] (DEPRECATED in favor of i18nKey) The text content
 * to show within the status message note: text string can be a translation key, it will
 * automatically be translated
 * @param {Object} [options.textValues={}] (DEPRECATED in favor of i18nKeyValues) a hash for
 * passing in for translation value support.
 * @param {number} [options.timeout=2000] The time (in ms) before the status message removes
 * itself from being shown.  *Note: Use timeout = 0 to force the user to close the message.*
 * @param {string} [options.template] a text string of the template id that should be rendered
 * @param {string} [options.templateUrl] a text string of the url location of the
 * template that should be rendered
 * @param {Scope} [options.scope=$rootScope] A scope instance to use for the status message
 * body content.
 * @returns {string} id of status message being shown on the screen
 */

/**
 * @ngdoc method
 * @name statusMessage#showError
 * @description shows an error message to the end user. *Note: The message must be closed by
 * the end user as there is no allowed timeout.*
 * @param {Object} [options] A hash with the options specified below.
 * @param {string|TranslateKey} [options.i18nKey] The i18n key or generic string content
 * to show within the status message.  If the value is found in the translate table, automatic
 * translation will take place.
 * @param {Object} [options.i18nKeyValues={}] a hash for passing in for translation value support.
 * @param {string|TranslateKey} [options.text] (DEPRECATED in favor of i18nKey) The text content
 * to show within the status message note: text string can be a translation key, it will
 * automatically be translated
 * @param {Object} [options.textValues={}] (DEPRECATED in favor of i18nKeyValues) a hash for
 * passing in for translation value support.
 * @param {string} [options.template] a text string of the template id that should be rendered
 * @param {string} [options.templateUrl] a text string of the url location of the
 * template that should be rendered
 * @param {Scope} [options.scope=$rootScope] A scope instance to use for the status message
 * body content.
 * @returns {string} id of status message being shown on the screen
 */

/**
 * @ngdoc method
 * @name statusMessage#showWarning
 * @description shows a warning message to the end user. *Note: The message must be closed by
 * the end user as there is no allowed timeout.*
 * @param {Object} [options] A hash with the options specified below.
 * @param {string|TranslateKey} [options.i18nKey] The i18n key or generic string content
 * to show within the status message.  If the value is found in the translate table, automatic
 * translation will take place.
 * @param {Object} [options.i18nKeyValues={}] a hash for passing in for translation value support.
 * @param {string|TranslateKey} [options.text] (DEPRECATED in favor of i18nKey) The text content
 * to show within the status message note: text string can be a translation key, it will
 * automatically be translated
 * @param {Object} [options.textValues={}] (DEPRECATED in favor of i18nKeyValues) a hash for
 * passing in for translation value support.
 * @param {string} [options.template] a text string of the template id that should be rendered
 * @param {string} [options.templateUrl] a text string of the url location of the
 * template that should be rendered
 * @param {Scope} [options.scope=$rootScope] A scope instance to use for the status message
 * body content.
 * @returns {string} id of status message being shown on the screen
 */
  .directive('akamStatusMessage', statusMessageDirective)

/**
 * @name akamStatusMessageGroup
 *
 * @description Creates a status message group control.
 *
 * @restrict E
 *
 * @param {Array} [items=[]] Status message object items
 * to display.
 *
 */
  .directive('akamStatusMessageGroup', statusMessageGroupDirective)

/**
 * @ngdoc service
 *
 * @name statusMessage
 *
 * @description Displays an action's status.
 *
 */
  .factory('statusMessage', statusMessageService);
